import { useRoleLabel } from '@/hooks/useRoleLabel';
import { useRoles } from '@/hooks/useRoles';
import { useAppStore } from '@/stores/app';
import type { Org } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Popover } from '@nautilusbio/nb-design-system/Popover';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { Check, KeyRound } from 'lucide-react';
import React, { type FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { SIDEBAR_ICON_CONFIG, sidebarTextSize } from '../constants';
import styles from '../styles.module.css';

import {
  POPOVER_OFFSET_PX,
  SIDEBAR_COLLAPSED_POPOVER_POSITIONS,
  SIDEBAR_POPOVER_POSITIONS,
} from './OrgSelector/constants';
import { useCurrentOrgAllowedRoles, useRoleSwitcher } from './OrgSelector/hooks';

export type RoleSwitcherProps = {
  isSidebarCollapsed: boolean;
  selectedOrg: Org;
};

/**
 * Sidebar row that switches the role assumed in the selected org. Gated on how many roles the
 * user holds in *that* org rather than on being an admin: any user with two or more can switch,
 * and fewer leaves the row inert rather than hidden, so the current role stays readable.
 */
const RoleSwitcher: FC<RoleSwitcherProps> = ({ isSidebarCollapsed, selectedOrg }) => {
  const { t } = useTranslation('sidebar');
  const roleLabel = useRoleLabel();
  const [isOpen, setIsOpen] = useState(false);

  const { isOrgSelectorOpen, isSidebarMenuOpen, setIsOrgSelectorOpen, setIsSidebarMenuOpen } = useAppStore(
    useShallow(state => ({
      isOrgSelectorOpen: state.isOrgSelectorOpen,
      isSidebarMenuOpen: state.isSidebarMenuOpen,
      setIsOrgSelectorOpen: state.setIsOrgSelectorOpen,
      setIsSidebarMenuOpen: state.setIsSidebarMenuOpen,
    })),
  );

  const allowedRoles = useCurrentOrgAllowedRoles(selectedOrg.id);
  const { getCurrentRole } = useRoles();
  const currentRole = getCurrentRole(selectedOrg.id);

  const close = useCallback(() => setIsOpen(false), []);
  const { handleSwitchRole, isAssumingRole } = useRoleSwitcher(selectedOrg, close);

  const canSwitch = allowedRoles.length > 1;

  // This popover's open state is local, so the sibling popovers — whose state lives in the store —
  // have no way to close it. Expanded, all three rows are mounted at once, so without this the
  // role menu stays open over whichever one the user opens next. Collapsed, this row lives inside
  // the user menu, so `isSidebarMenuOpen` is true whenever it is reachable at all and must not
  // count as a competing popover.
  useEffect(() => {
    // oxlint-disable-next-line set-state-in-effect -- isOpen is also user-toggled directly (handleToggle below), so this is a real sync with a sibling's store-owned state, not derivable
    if (isOrgSelectorOpen || (!isSidebarCollapsed && isSidebarMenuOpen)) setIsOpen(false);
  }, [isOrgSelectorOpen, isSidebarMenuOpen, isSidebarCollapsed]);

  const handleToggle = useCallback(() => {
    if (!canSwitch) return;
    setIsOpen(open => !open);
    setIsOrgSelectorOpen(false);
    if (!isSidebarCollapsed) setIsSidebarMenuOpen(false);
  }, [canSwitch, isSidebarCollapsed, setIsOrgSelectorOpen, setIsSidebarMenuOpen]);

  const { size, strokeWidth } = isSidebarCollapsed ? SIDEBAR_ICON_CONFIG.collapsed : SIDEBAR_ICON_CONFIG.expanded;

  let rowLabel = t('role_none');
  if (currentRole) rowLabel = roleLabel(currentRole);
  else if (allowedRoles.length > 0) rowLabel = t('role_select');

  // "Switch role" would be a lie on the inert row, which is the one a user is most likely to hover.
  // The inert row spans three states and only one of them has a role to name, so `role_only` —
  // "Your only role in this organization: {{role}}" — can't be the label for the other two.
  let tooltip = t('role_none');
  if (canSwitch) tooltip = t('role_switcher');
  else if (currentRole) tooltip = t('role_only', { role: roleLabel(currentRole) });
  else if (allowedRoles.length > 0) tooltip = t('role_select');

  const popoverContent = (
    <Stack className={styles.popoverContainerWidth} gap={2} orientation="vertical">
      <Text as="h4">{t('roles')}</Text>

      {allowedRoles.map(role => {
        const isActive = role === currentRole;

        return (
          <Button
            aria-current={isActive}
            data-testid={`sidebar-role-option-${role}`}
            fullWidth
            isDisabled={isActive || isAssumingRole}
            key={role}
            onPress={() => handleSwitchRole(selectedOrg, role)}
            size="xs"
            visual={isActive ? 'success' : 'outline'}
          >
            <Stack alignItems="center" as="span" gap={1} orientation="horizontal">
              {roleLabel(role)}

              {isActive && <Check size={10} />}
            </Stack>
          </Button>
        );
      })}
    </Stack>
  );

  return (
    <Popover
      align="start"
      content={popoverContent}
      className={styles.popoverContent}
      data-testid="sidebar-role-switcher-popover"
      isOpen={isOpen}
      onClickOutside={close}
      padding={POPOVER_OFFSET_PX}
      positions={isSidebarCollapsed ? SIDEBAR_COLLAPSED_POPOVER_POSITIONS : SIDEBAR_POPOVER_POSITIONS}
      styled
    >
      {/* aria-disabled, not isDisabled: disabling drops the row from the tab order, hiding it from
          the users it exists for; handleToggle no-ops instead. The DS Button re-sets aria-disabled
          from isDisabled after spreading caller props, so it never reaches the DOM — inertness is
          behavioural only until that upstream fix ships. data-can-switch is the interim DOM signal
          Playwright's test.skip guards read instead (see tests/sidebar.pw.ts,
          tests/role-switch-grid-refresh.pw.ts), since it isn't a prop the Button overwrites. */}
      <Button
        aria-disabled={!canSwitch}
        aria-expanded={canSwitch ? isOpen : undefined}
        aria-haspopup={canSwitch ? 'menu' : undefined}
        aria-label={tooltip}
        className={styles.sidebarLink}
        data-can-switch={canSwitch}
        data-testid="sidebar-role-switcher-trigger"
        fullWidth
        onPress={handleToggle}
        visual="unstyled"
      >
        <KeyRound
          className={isSidebarCollapsed ? styles.iconIsCollapsed : undefined}
          size={size}
          strokeWidth={strokeWidth}
        />

        <Text as="span" className={styles.footerButtonText} noWrap size={sidebarTextSize}>
          {rowLabel}
        </Text>
      </Button>
    </Popover>
  );
};

export default RoleSwitcher;
